import { useState } from "react";
import { Alert, Button } from "react-bootstrap";

import { useQueryClient } from "@tanstack/react-query";

import {
  CrossValidateRequest,
  CrossValidateResult,
  CrossValidateSaveRequest,
  SavedModel,
} from "../data/CrossValidation";
import { useCrossValidation } from "../hooks/useCrossValidation";
import { CrossValidationForm } from "./CrossValidationForm";
import { CrossValidationResults } from "./CrossValidationResults";
import { DefaultModelSection } from "./DefaultModelSection";
import { SaveModelModal, SaveModelValues } from "./SaveModelModal";

export function Preferences(): JSX.Element {
  const { runCrossValidation, saveModel, setDefaultModel } =
    useCrossValidation();
  const queryClient = useQueryClient();

  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<CrossValidateResult | null>(null);
  // Hyperparameters aren't echoed back in the CV result, so we cache the
  // request to forward them on save (training_config drives recalibrate).
  const [lastRequest, setLastRequest] = useState<CrossValidateRequest | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedModel, setSavedModel] = useState<SavedModel | null>(null);

  const handleSetDefault = async () => {
    if (!savedModel) return;
    try {
      await setDefaultModel(savedModel.id);
      await queryClient.invalidateQueries({ queryKey: ["userSettings"] });
      setSavedModel(null);
      setSuccessMessage(
        `Model "${savedModel.name}" is now the default.`,
      );
    } catch (e: unknown) {
      setError((e as Error).message);
    }
  };

  const handleRunCrossValidation = async (request: CrossValidateRequest) => {
    setIsRunning(true);
    setError(null);
    setResult(null);
    setLastRequest(null);
    setSuccessMessage(null);
    setSavedModel(null);
    try {
      const cvResult = await runCrossValidation(request);
      setResult(cvResult);
      setLastRequest(request);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSave = async (values: SaveModelValues) => {
    if (!result) return;

    setIsSaving(true);
    setSaveError(undefined);
    try {
      const hyperparams: Partial<CrossValidateSaveRequest> = lastRequest
        ? {
            ...(lastRequest.threshold !== undefined && {
              threshold: lastRequest.threshold,
            }),
            ...(lastRequest.margin !== undefined && {
              margin: lastRequest.margin,
            }),
            ...(lastRequest.min_df !== undefined && {
              min_df: lastRequest.min_df,
            }),
            ...(lastRequest.max_df !== undefined && {
              max_df: lastRequest.max_df,
            }),
            ...(lastRequest.alpha !== undefined && {
              alpha: lastRequest.alpha,
            }),
            ...(lastRequest.calibration_cv !== undefined && {
              calibration_cv: lastRequest.calibration_cv,
            }),
            ...(lastRequest.min_category_samples !== undefined && {
              min_category_samples: lastRequest.min_category_samples,
            }),
          }
        : {};
      const request: CrossValidateSaveRequest = {
        name: values.name,
        from_date: result.from_date,
        to_date: result.to_date,
        recalibrate_full: values.recalibrate_full,
        set_as_default: values.set_as_default,
        implementation: result.implementation,
        ...(!values.recalibrate_full && {
          split_ratio: result.split_ratio,
          random_seed: result.random_seed,
        }),
        ...hyperparams,
      };
      const saved = await saveModel(request);
      await queryClient.invalidateQueries({ queryKey: ["categorisors"] });
      if (values.set_as_default) {
        await queryClient.invalidateQueries({ queryKey: ["userSettings"] });
      }
      setShowSaveModal(false);
      setResult(null);
      setLastRequest(null);
      setSavedModel(values.set_as_default ? null : saved);
      setSuccessMessage(
        values.set_as_default
          ? `Model "${values.name}" saved and set as default.`
          : `Model "${values.name}" saved successfully.`,
      );
    } catch (e: unknown) {
      setSaveError((e as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2>Preferences</h2>

      <DefaultModelSection />
      <hr />

      <h4>Model Calibration</h4>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => {
            setSuccessMessage(null);
            setSavedModel(null);
          }}
        >
          {successMessage}
          {savedModel && (
            <Button
              variant="outline-success"
              size="sm"
              className="ms-3"
              onClick={handleSetDefault}
            >
              Set as Default
            </Button>
          )}
        </Alert>
      )}

      <CrossValidationForm
        onSubmit={handleRunCrossValidation}
        isRunning={isRunning}
      />

      {result && (
        <div className="mt-4">
          <CrossValidationResults
            result={result}
            onSave={() => setShowSaveModal(true)}
          />
        </div>
      )}

      <SaveModelModal
        show={showSaveModal}
        onHide={() => {
          setShowSaveModal(false);
          setSaveError(undefined);
        }}
        onSave={handleSave}
        isSaving={isSaving}
        error={saveError}
      />
    </div>
  );
}
